import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, RefreshCw, Database, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import ClientPicker from "@/components/ClientPicker";

// One-click (re)ingest of the deterministic QA sample set (3 test protocols,
// 4 budgets, 4 CTAs) for a client — no LLM, ready in seconds.
export default function QaSampleReseedDialog({ defaultClientId, onClose, onDone }) {
    const [clientId, setClientId] = useState(defaultClientId || "");
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [purge, setPurge] = useState(true);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        setStatus(null); setResult(null);
        if (!clientId) return;
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/admin/study-assets/qa-samples/status`, { params: { client_id: clientId } });
                if (alive) setStatus(data);
            } catch (e) { toast.error(formatApiErrorDetail(e)); }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [clientId]);

    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/study-assets/qa-samples/reseed`, {
                client_id: clientId, purge_unreadable: purge && (status?.unreadable?.total || 0) > 0,
            });
            setResult(data);
            toast.success(`Sample data ready: ${data.counts.protocols} protocols · ${data.counts.budgets} budgets · ${data.counts.ctas} CTAs`);
            onDone?.(data);
        } catch (e) { toast.error(formatApiErrorDetail(e)); }
        finally { setBusy(false); }
    };

    const unreadable = status?.unreadable?.total || 0;
    const samples = status?.samples || {};
    const plan = status?.plan || {};

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-lg p-6" onMouseDown={(e) => e.stopPropagation()} data-testid="qa-sample-dialog">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h2 className="font-display text-2xl">Re-ingest sample budgets &amp; CTAs</h2>
                        <p className="text-xs text-white/50 mt-1">Restores a ready-to-use QA set: 3 test protocols, 4 budgets and 4 CTAs across three studies. Any earlier sample set is replaced.</p>
                    </div>
                    <button onClick={onClose} data-testid="qa-sample-close"><X className="w-5 h-5 text-white/50" /></button>
                </div>
                <div className="space-y-3">
                    <ClientPicker value={clientId} onChange={setClientId} label="Client" required excludeDisabled includeSuperdomain testId="qa-sample-client" />
                    {loading && <div className="text-xs text-white/50" data-testid="qa-sample-loading">Checking current data…</div>}
                    {status && !result && (
                        <div className="rounded-xl border border-white/10 divide-y divide-white/5 text-sm" data-testid="qa-sample-status">
                            <Row icon={<Database className="w-4 h-4 text-[#c7a6ff]" />} label="Sample set present" testid="qa-sample-present"
                                value={samples.protocols || samples.budgets || samples.ctas
                                    ? `${samples.protocols} protocols · ${samples.budgets} budgets · ${samples.ctas} CTAs${status.last_seeded_at ? ` · seeded ${status.last_seeded_at.slice(0, 10)}` : ""}`
                                    : "none yet"} />
                            <Row icon={<RefreshCw className="w-4 h-4 text-emerald-300" />} label="Will create" testid="qa-sample-plan"
                                value={`${plan.protocols} protocols · ${plan.budgets} budgets · ${plan.ctas} CTAs (ready immediately, no AI call)`} />
                            {unreadable > 0 && (
                                <div className="p-3 flex items-start gap-2" data-testid="qa-sample-unreadable">
                                    <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                                    <label className="flex-1 cursor-pointer">
                                        <div className="text-amber-100/90">{unreadable} document{unreadable === 1 ? "" : "s"} can no longer be decrypted
                                            <span className="text-white/50"> — {status.unreadable.budgets} budgets · {status.unreadable.ctas} CTAs · {status.unreadable.study_builds} study builds</span></div>
                                        <div className="flex items-center gap-2 mt-1.5 text-xs text-white/70">
                                            <input type="checkbox" checked={purge} onChange={(e) => setPurge(e.target.checked)} className="accent-[#7C3AED]" data-testid="qa-sample-purge" />
                                            Remove them as part of the re-ingest (super admin only)
                                        </div>
                                    </label>
                                </div>
                            )}
                        </div>
                    )}
                    {result && (
                        <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/5 p-3 text-sm" data-testid="qa-sample-result">
                            <div className="flex items-center gap-2 text-emerald-200"><CheckCircle2 className="w-4 h-4" /> Sample data re-ingested for <span className="font-mono">{result.client_id}</span></div>
                            <ul className="mt-2 space-y-1 text-xs text-white/70">
                                {result.created.protocols.map((p) => (
                                    <li key={p.id} data-testid={`qa-sample-proto-${p.protocol_number}`}>
                                        <span className="font-mono text-[#c7a6ff]">{p.protocol_number}</span> · {p.sponsor} — {result.created.budgets.filter((b) => b.protocol_number === p.protocol_number).length} budget(s), {result.created.ctas.filter((c) => c.protocol_number === p.protocol_number).length} CTA(s)
                                    </li>
                                ))}
                            </ul>
                            {(result.removed_unreadable.budgets + result.removed_unreadable.ctas + result.removed_unreadable.study_builds) > 0 && (
                                <div className="mt-2 text-xs text-white/50" data-testid="qa-sample-removed">
                                    Removed undecryptable: {result.removed_unreadable.budgets} budgets · {result.removed_unreadable.ctas} CTAs · {result.removed_unreadable.study_builds} study builds
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-white/60" data-testid="qa-sample-cancel">{result ? "Close" : "Cancel"}</button>
                    {!result && (
                        <button onClick={run} disabled={busy || !clientId || loading} data-testid="qa-sample-run"
                            className="px-4 py-2 rounded-full bg-[#7C3AED] text-white text-sm disabled:opacity-50 flex items-center gap-2">
                            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Re-ingesting…" : samples.budgets ? "Replace sample set" : "Re-ingest sample data"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Row({ icon, label, value, testid }) {
    return (
        <div className="p-3 flex items-start gap-2" data-testid={testid}>
            {icon}
            <div className="flex-1"><div className="text-white/50 text-xs">{label}</div><div className="text-white/85">{value}</div></div>
        </div>
    );
}
